<script setup lang="ts">
    import { computed, ref, type WritableComputedRef } from 'vue';
    import moment from 'moment';
    import FilterCheckboxList from './FilterCheckboxList.vue';
    import {
        ALL_SPECIFICATION_TYPES,
        DATE_PRESETS,
        RELEASE_STATUS_GROUPS,
        narrowLibrariesByGroup,
        specificationTypeLabel,
        type ReleaseSearchFilterState,
        type ReleaseStatusGroupKey
    } from './useReleaseSearchFilters';
    import type { ReleaseSearchLibrary, ReleaseSpecificationType } from '@/shared/helpers/api';

    const props = defineProps<{
        filters: ReleaseSearchFilterState;
        libraryOptions: ReleaseSearchLibrary[];
        scopeOptions: string[];
        lookupsLoading: boolean;
    }>();

    const emit = defineEmits<{
        update: [patch: Partial<ReleaseSearchFilterState>];
        clear: [];
    }>();

    /**
     * The filter state is owned by the parent, so the panel reports changes as
     * patches instead of writing through the prop.
     */
    function field<K extends keyof ReleaseSearchFilterState>(key: K) {
        return computed<ReleaseSearchFilterState[K]>({
            get: () => props.filters[key],
            set: value => emit('update', { [key]: value } as Partial<ReleaseSearchFilterState>)
        });
    }

    const libraries = field('libraries');
    const scopes = field('scopes');
    const datePreset = field('datePreset');
    const createdFrom = field('createdFrom');
    const createdTo = field('createdTo');

    /**
     * The filter state keeps the dates as yyyy-MM-dd strings so they read well
     * in the URL, while the datepicker works in Date objects. The conversion
     * stays local so the string form remains the one true representation.
     */
    function dateField(text: WritableComputedRef<string | null>) {
        return computed<Date | null>({
            get: () => {
                if (text.value === null) return null;
                const parsed = moment(text.value, 'YYYY-MM-DD', true);
                return parsed.isValid() ? parsed.toDate() : null;
            },
            set: value => { text.value = value === null ? null : formatDate(value); }
        });
    }

    function formatDate(date: Date): string {
        return moment(date).format('YYYY-MM-DD');
    }

    /**
     * Buefy parses typed input against the browser locale by default, which
     * would not read back what the formatter wrote.
     */
    function parseDate(text: string): Date | null {
        const parsed = moment(text, 'YYYY-MM-DD', true);
        return parsed.isValid() ? parsed.toDate() : null;
    }

    const createdFromDate = dateField(createdFrom);
    const createdToDate = dateField(createdTo);
    const onlyMine = field('onlyMine');
    const createdByContains = field('createdByContains');

    function toggleStatusGroup(group: ReleaseStatusGroupKey): void {
        emit('update', {
            statusGroups: props.filters.statusGroups.includes(group)
                ? props.filters.statusGroups.filter(g => g !== group)
                : [...props.filters.statusGroups, group]
        });
    }

    function toggleType(type: ReleaseSpecificationType): void {
        emit('update', {
            specificationTypes: props.filters.specificationTypes.includes(type)
                ? props.filters.specificationTypes.filter(t => t !== type)
                : [...props.filters.specificationTypes, type]
        });
    }

    /**
     * The library groups narrow which libraries are offered, not which releases
     * match, so the choice is view state and deliberately stays out of the URL.
     */
    const selectedLibraryGroups = ref<string[]>([]);

    const libraryGroupOptions = computed<string[]>(() =>
        [...new Set(props.libraryOptions.flatMap(l => l.groups))].sort());

    const visibleLibraryNames = computed<string[]>(() =>
        narrowLibrariesByGroup(props.libraryOptions, selectedLibraryGroups.value, props.filters.libraries));

    function toggleLibraryGroup(group: string): void {
        selectedLibraryGroups.value = selectedLibraryGroups.value.includes(group)
            ? selectedLibraryGroups.value.filter(g => g !== group)
            : [...selectedLibraryGroups.value, group];
    }
</script>

<template>
    <aside class="filter-panel">
        <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
            <h2 class="title is-6 mb-0">
                Filters
            </h2>
            <b-button
                type="is-text"
                @click="emit('clear')">
                Clear all
            </b-button>
        </div>

        <b-collapse
            :model-value="true"
            class="filter-section"
            animation="slide">
            <template #trigger="collapseProps">
                <div
                    class="filter-section-header"
                    role="button">
                    <b-icon :icon="collapseProps.open ? 'menu-down' : 'menu-right'" />
                    <span>Status</span>
                    <span
                        v-if="!collapseProps.open && filters.statusGroups.length > 0"
                        class="section-count">
                        {{ filters.statusGroups.length }}
                    </span>
                </div>
            </template>
            <div class="filter-section-body">
                <b-checkbox
                    v-for="group in RELEASE_STATUS_GROUPS"
                    :key="group.key"
                    :model-value="filters.statusGroups.includes(group.key)"
                    class="option"
                    @update:model-value="toggleStatusGroup(group.key)">
                    <span class="is-flex is-align-items-center">
                        <b-icon
                            :icon="group.icon"
                            :class="group.colorClass"
                            size="is-small"
                            class="mr-1" />
                        {{ group.label }}
                    </span>
                </b-checkbox>
            </div>
        </b-collapse>

        <b-collapse
            :model-value="false"
            class="filter-section"
            animation="slide">
            <template #trigger="collapseProps">
                <div
                    class="filter-section-header"
                    role="button">
                    <b-icon :icon="collapseProps.open ? 'menu-down' : 'menu-right'" />
                    <span>Release type</span>
                    <span
                        v-if="!collapseProps.open && filters.specificationTypes.length > 0"
                        class="section-count">
                        {{ filters.specificationTypes.length }}
                    </span>
                </div>
            </template>
            <div class="filter-section-body">
                <b-checkbox
                    v-for="type in ALL_SPECIFICATION_TYPES"
                    :key="type"
                    :model-value="filters.specificationTypes.includes(type)"
                    class="option"
                    @update:model-value="toggleType(type)">
                    {{ specificationTypeLabel(type) }}
                </b-checkbox>
            </div>
        </b-collapse>

        <b-collapse
            :model-value="true"
            class="filter-section"
            animation="slide">
            <template #trigger="collapseProps">
                <div
                    class="filter-section-header"
                    role="button">
                    <b-icon :icon="collapseProps.open ? 'menu-down' : 'menu-right'" />
                    <span>Library</span>
                    <span
                        v-if="!collapseProps.open && filters.libraries.length > 0"
                        class="section-count">
                        {{ filters.libraries.length }}
                    </span>
                </div>
            </template>
            <div class="filter-section-body">
                <filter-checkbox-list
                    v-model="libraries"
                    :options="visibleLibraryNames"
                    placeholder="Search libraries..."
                    :empty-text="lookupsLoading ? 'Loading...' : 'No libraries with releases'"
                    :search-threshold="0">
                    <template #search-addon>
                        <b-dropdown
                            v-if="libraryGroupOptions.length > 0"
                            :scrollable="true"
                            :max-height="260"
                            aria-role="list"
                            class="group-dropdown">
                            <template #trigger>
                                <b-button
                                    :title="selectedLibraryGroups.length > 0
                                        ? `Showing ${selectedLibraryGroups.length} group(s)`
                                        : 'Narrow the list by library group'"
                                    :icon-left="selectedLibraryGroups.length > 0 ? 'filter' : 'filter-outline'"
                                    :label="selectedLibraryGroups.length > 0
                                        ? String(selectedLibraryGroups.length)
                                        : ''"
                                    class="is-info"
                                    size="is-small" />
                            </template>
                            <b-dropdown-item
                                v-if="selectedLibraryGroups.length > 0"
                                aria-role="listitem"
                                @click="selectedLibraryGroups = []">
                                <span class="has-text-weight-semibold">Show all groups</span>
                            </b-dropdown-item>
                            <hr
                                v-if="selectedLibraryGroups.length > 0"
                                class="dropdown-divider">
                            <b-dropdown-item
                                v-for="group in libraryGroupOptions"
                                :key="group"
                                :focusable="false"
                                aria-role="listitem"
                                custom>
                                <b-checkbox
                                    :model-value="selectedLibraryGroups.includes(group)"
                                    @update:model-value="toggleLibraryGroup(group)">
                                    {{ group }}
                                </b-checkbox>
                            </b-dropdown-item>
                        </b-dropdown>
                    </template>
                </filter-checkbox-list>
            </div>
        </b-collapse>

        <b-collapse
            :model-value="false"
            class="filter-section"
            animation="slide">
            <template #trigger="collapseProps">
                <div
                    class="filter-section-header"
                    role="button">
                    <b-icon :icon="collapseProps.open ? 'menu-down' : 'menu-right'" />
                    <span>Scopes</span>
                    <span
                        v-if="!collapseProps.open && filters.scopes.length > 0"
                        class="section-count">
                        {{ filters.scopes.length }}
                    </span>
                </div>
            </template>
            <div class="filter-section-body">
                <filter-checkbox-list
                    v-model="scopes"
                    :options="scopeOptions"
                    placeholder="Search scopes..."
                    :empty-text="lookupsLoading ? 'Loading...' : 'No scopes with releases'" />
            </div>
        </b-collapse>

        <b-collapse
            :model-value="false"
            class="filter-section"
            animation="slide">
            <template #trigger="collapseProps">
                <div
                    class="filter-section-header"
                    role="button">
                    <b-icon :icon="collapseProps.open ? 'menu-down' : 'menu-right'" />
                    <span>Created</span>
                    <span
                        v-if="!collapseProps.open && filters.datePreset !== 'any'"
                        class="section-count">
                        1
                    </span>
                </div>
            </template>
            <div class="filter-section-body">
                <b-radio
                    v-for="preset in DATE_PRESETS"
                    :key="preset.key"
                    v-model="datePreset"
                    :native-value="preset.key"
                    class="option">
                    {{ preset.label }}
                </b-radio>
                <div
                    v-if="filters.datePreset === 'custom'"
                    class="mt-2">
                    <b-field
                        label="From"
                        custom-class="is-small">
                        <b-datepicker
                            v-model="createdFromDate"
                            :date-formatter="formatDate"
                            :date-parser="parseDate"
                            :max-date="createdToDate ?? undefined"
                            placeholder="Any"
                            icon="calendar-today"
                            editable
                            trap-focus />
                    </b-field>
                    <b-field
                        label="To"
                        custom-class="is-small">
                        <b-datepicker
                            v-model="createdToDate"
                            :date-formatter="formatDate"
                            :date-parser="parseDate"
                            :min-date="createdFromDate ?? undefined"
                            placeholder="Any"
                            icon="calendar-today"
                            editable
                            trap-focus />
                    </b-field>
                </div>
            </div>
        </b-collapse>

        <b-collapse
            :model-value="false"
            class="filter-section"
            animation="slide">
            <template #trigger="collapseProps">
                <div
                    class="filter-section-header"
                    role="button">
                    <b-icon :icon="collapseProps.open ? 'menu-down' : 'menu-right'" />
                    <span>Created by</span>
                    <span
                        v-if="!collapseProps.open && (filters.onlyMine || filters.createdByContains)"
                        class="section-count">
                        1
                    </span>
                </div>
            </template>
            <div class="filter-section-body">
                <b-checkbox
                    v-model="onlyMine"
                    class="mb-2">
                    Only my releases
                </b-checkbox>
                <b-input
                    v-model="createdByContains"
                    :disabled="filters.onlyMine"
                    placeholder="Name or e-mail contains..."
                    type="search"
                    icon="account-search" />
            </div>
        </b-collapse>
    </aside>
</template>

<style scoped>
  .filter-panel {
    width: 280px;
    min-width: 280px;
  }

  .filter-section {
    border-bottom: 1px solid #dbdbdb;
    margin-bottom: 0.5rem;
  }

  .filter-section-header {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-weight: 600;
    padding: 0.4rem 0;
    user-select: none;
  }

  .filter-section-body {
    padding: 0 0 0.75rem 0.25rem;
  }

  .section-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 0.4rem;
    min-width: 1.25rem;
    padding: 0 0.3rem;
    border-radius: 1rem;
    background-color: var(--eq-norwegian-woods-1);
    color: var(--eq-white);
    font-size: 0.7rem;
    font-weight: 600;
    line-height: 1.25rem;
  }

  .option {
    display: flex;
    width: 100%;
    margin-bottom: 0.15rem;
  }

  /* The panel is only 250px, so let the group list overflow it rather than
     squeeze the group names into an unreadable column. */
  .group-dropdown :deep(.dropdown-menu) {
    min-width: 16rem;
  }

  .group-dropdown :deep(.dropdown-item) {
    padding-right: 1rem;
  }
</style>
